import { Locale } from '../ruleset/locale';
import { duration } from './duration';
import { exhaustiveTuple } from './types';
import { DateTime } from 'luxon';

export const JST_UTC_OFFSET = duration({ hours: 9 });
export const getJSTOffset = (date: Date): number =>
  JST_UTC_OFFSET + duration({ minutes: date.getTimezoneOffset() });

export function getJSTDate(...args: ConstructorParameters<typeof Date>): Date {
  const date = new Date(...args);
  date.setTime(date.getTime() + getJSTOffset(date));
  return date;
}

/**
 * Get a DateTime object in JST. Everything set or retrieved from this object will be in JST.
 * @param args - arguments to pass to Date constructor
 * @returns
 */
export function getJSTDateTime(now: number) {
  return DateTime.fromMillis(now).setZone('Asia/Tokyo');
}

export function setJSTHours(
  date: Date,
  hours: number,
  minutes = 0,
  seconds = 0,
  miliseconds = 0,
) {
  date.setUTCHours(hours, minutes, seconds, miliseconds);
  date.setTime(date.getTime() - JST_UTC_OFFSET);
}

export const formatJSTTime = (
  now: number,
  dateStyle: TimeUnitStyle = 'medium',
  timeStyle: TimeUnitStyle = 'long',
): string =>
  new Date(now).toLocaleString('en-US', {
    timeZone: 'Asia/Tokyo',
    dateStyle,
    timeStyle,
  });

type TimeUnitStyle = 'full' | 'long' | 'medium' | 'short' | undefined;

export function formatDuration(duration: number) {
  duration = Math.floor(duration / 1000);
  const seconds = duration % 60;

  duration = Math.floor(duration / 60);
  const minutes = duration % 60;

  duration = Math.floor(duration / 60);
  const hours = Math.floor(duration);

  const hoursStr = hours > 0 ? `${hours}:` : '';
  const minutesPrefix = hours === 0 || minutes >= 10 ? '' : '0';
  const minutesStr = `${minutesPrefix}${minutes}:`;
  const secondsPrefix = seconds >= 10 ? '' : '0';
  const secondsStr = `${secondsPrefix}${seconds}`;

  return `${hoursStr}${minutesStr}${secondsStr}`;
}

export function formatDurationShort(duration: number, locale: Locale) {
  duration = Math.floor(duration / 1000 / 60);
  const minutes = duration % 60;

  duration = Math.floor(duration / 60);
  const hours = Math.floor(duration);

  const hoursStr = hours > 0 ? `${hours}` : '';
  const minutesPrefix = hours === 0 || minutes >= 10 ? '' : '0';
  const minutesStr = `${minutesPrefix}${minutes}`;

  switch (locale) {
    case 'ja':
      return `${hoursStr}時間${minutesStr}分`;
    default:
      return `${hoursStr} hours ${minutesStr} minutes`;
  }
}

/**
 * Get difference in days in JST
 */
export function getJSTDaysDiff(now: number, timestamp: number): number {
  const jstNow = getJSTDate(now);
  jstNow.setHours(0, 0, 0, 0);

  const jstTime = getJSTDate(timestamp);
  jstTime.setHours(0, 0, 0, 0);

  return (jstNow.getTime() - jstTime.getTime()) / duration({ days: 1 });
}

export type DaysOfTheWeek =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export const sortedDaysOfTheWeek = exhaustiveTuple<DaysOfTheWeek>()(
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
);

/**
 * Get day of the week in JST.
 * @param now
 */
export function getJSTDayOfTheWeek(now: number): DaysOfTheWeek {
  const date = getJSTDate(now);
  const day = date.getDay();
  return sortedDaysOfTheWeek[day];
}
